import { type JSX, useMemo } from "react";

import { type ContractModel, type EntityModel, type GSuiteAssetModel } from "@doitintl/cmp-models";
import { Box, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import values from "lodash/values";

import DataCouplet from "../../../Components/DataCouplet";
import { useAuthContext } from "../../../Context/AuthContext";
import { type Entity } from "../../../Context/customer/EntitiesContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { type Asset } from "../../../types";
import { formatNumber, humanBoolean } from "../../../utils/common";
import { NotAvailable } from "../../Assets/Tabs/components/typographics";
import { contractDate } from "../utils";
import GeneralSection from "./components/GeneralSection";
import { getCommitmentTypeText } from "./utils";

export type ContractPageGSuiteProps = {
  contract: ContractModel;
  accountManagerName?: string;
  contractId: string;
  isSidePanel?: boolean;
};

export const cyIds = {
  text: {
    title: "title",
  },
  sections: {
    general: "general-section",
    details: "details-section",
    commitment: "commitment-section",
  },
};

const ContractPageGsuite = ({ contract, accountManagerName, contractId, isSidePanel }: ContractPageGSuiteProps) => {
  const { entities, entitiesLoading, assets, assetsLoading } = useCustomerContext();
  const { isDoitEmployee } = useAuthContext();
  const entity = useMemo<Entity | undefined>(
    () => entities.find((ent) => ent.id === contract.entity?.id),
    [contract.entity?.id, entities]
  );

  const subscriptions = useMemo<string | JSX.Element>(() => {
    const isGsuite = (asset: Asset<any>): asset is Asset<GSuiteAssetModel> => asset.data.type === "g-suite";

    const subscriptions = values(assets)
      .flatMap((assetList) => assetList)
      .filter((asset) => isGsuite(asset))
      .filter((asset) => asset.data.entity?.id === entity?.id && asset.data.contract?.id === contractId)
      .map((asset) => asset.data.properties.subscription)
      .filter((subscriptions) => !!subscriptions)
      .flat();

    if (!subscriptions.length) {
      return NotAvailable;
    }

    return (
      <List>
        {subscriptions.map((subscription) => (
          <ListItem key={subscription.skuId} sx={{ py: 0, pl: 0 }}>
            <ListItemText>{subscription.skuName}</ListItemText>
          </ListItem>
        ))}
      </List>
    );
  }, [assets, contractId, entity?.id]);

  return (
    <Stack spacing={3}>
      {!isSidePanel && (
        <Typography variant="h1" data-cy={cyIds.text.title}>
          Google Workspace
        </Typography>
      )}
      <GeneralSection
        entity={entity as EntityModel}
        entitiesLoading={entitiesLoading}
        accountManagerName={accountManagerName || NotAvailable}
        contract={contract}
        showAnnualizedValue={true}
      />
      <Box component="section" data-cy={cyIds.sections.details}>
        <Typography variant="h2">Contract Details</Typography>
        <Stack component="dl" spacing={1}>
          <DataCouplet
            key="commitment-deal"
            field="Commitment deal"
            value={getCommitmentTypeText(contract)}
            divider={true}
          />
          <DataCouplet
            field="Renewal contract"
            value={humanBoolean(contract.isRenewal)}
            key="renewal-contract"
            divider={true}
          />
          <DataCouplet
            field="Start date"
            value={contractDate(contract.startDate)}
            key="contract-start-date"
            divider={true}
          />
          <DataCouplet
            field="End date"
            value={contractDate(contract?.endDate)}
            key="contract-end-date"
            divider={true}
          />
          <DataCouplet
            field="Discount"
            value={contract.discount ? `${contract.discount}%` : NotAvailable}
            key="contract-discount"
            divider={true}
          />
          <DataCouplet
            field="Special discount"
            value={
              contract.properties?.specialDiscount ? `${formatNumber(contract.properties.specialDiscount, 2)}%` : "-"
            }
            key="contract-special-discount"
            divider={true}
          />
          <DataCouplet
            field="Subscriptions"
            value={subscriptions}
            key="contract-subscriptions"
            loading={assetsLoading || entitiesLoading}
            divider={true}
          />
          {isDoitEmployee && (
            <DataCouplet
              key="margin"
              field="Margin"
              value={contract.partnerMargin ? `${contract.partnerMargin}%` : NotAvailable}
              divider={true}
            />
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default ContractPageGsuite;
